import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Upload, Play, Pause, RefreshCw, Download, Share2,
  ChevronRight, ChevronLeft, Check, Loader2, Wand2,
  Film, Camera, Eye, Package, Globe, User, Home, Sun, 
  Dumbbell, Moon, Sparkles, Volume2, VolumeX, X, Mic, Video,
  FlaskConical, Type, Ghost, Image, Plus, Shirt, Car, LayoutGrid,
  Building2, Store, Users, Coffee, Heart, Star, FolderOpen, AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ugcAPI, videosAPI, ghostAPI, voiceAPI, stockFootageAPI } from "../lib/api";
import { UpgradeModal } from "../components/UpgradeModal";
import { ABTestingModal } from "../components/ABTestingModal";
import { CaptionStyleSelector } from "../components/CaptionStyleSelector";
import GhostModePanel from "../components/GhostModePanel";
import { StockFootageToggle } from "../components/StockFootageToggle";
import TemplatePreviewGallery from "../components/TemplatePreviewGallery";
import { PickFromGalleryButton } from "../components/ImageGalleryPicker";
import MediaPickerModal, { MediaPickerButton } from "../components/MediaPickerModal";
import { useScriptFit } from "../components/ugc/useScriptFit";
import { ScriptFitIndicator } from "../components/ugc/ScriptFitIndicator";
import { ScriptFitPanel } from "../components/ugc/ScriptFitPanel";
import { SecondItemPicker } from "../components/ugc/SecondItemPicker";
import { LowCreditNudge } from "../components/ugc/LowCreditNudge";
import { saveUgcDraft, takeUgcDraft } from "../lib/ugcDraft";
import { UgcQualityPicker } from "../components/ugc/UgcQualityPicker";
import { ugcAdCost } from "../lib/ugcPricing";
import { MustShowField } from "../components/ugc/MustShowField";
import { LogoUpload } from "../components/ugc/LogoUpload";
import { OutfitPreviewDialog } from "../components/ugc/OutfitPreviewDialog";

const OUTFIT_PREVIEW_TYPES = ["clothing", "shoes", "accessories", "jewelry", "watch", "wig"];

// Compact config data
const PERSONAS = [
  { id: "british", label: "British", flag: "🇬🇧" },
  { id: "american", label: "American", flag: "🇺🇸" },
  { id: "french", label: "French", flag: "🇫🇷" },
  { id: "african", label: "Nigerian", flag: "🇳🇬" },
  { id: "ghanaian", label: "Ghanaian", flag: "🇬🇭" },
  { id: "indian", label: "Indian", flag: "🇮🇳" },
  { id: "asian", label: "Asian", flag: "🇯🇵" },
];

const SCENES = [
  // Home/Residential
  { id: "kitchen", label: "Kitchen", icon: Home, category: "Home" },
  { id: "living_room", label: "Living Room", icon: Home, category: "Home" },
  { id: "bedroom", label: "Bedroom", icon: Moon, category: "Home" },
  { id: "outdoor", label: "Outdoor", icon: Sun, category: "Home" },
  { id: "gym", label: "Home Gym", icon: Dumbbell, category: "Home" },
  { id: "evening_lounge", label: "Lounge", icon: Moon, category: "Home" },
  
  // Professional/Business
  { id: "office", label: "Office", icon: Building2, category: "Business" },
  { id: "showroom", label: "Showroom", icon: Store, category: "Business" },
  { id: "warehouse", label: "Warehouse", icon: Package, category: "Business" },
  { id: "conference_room", label: "Conference Room", icon: Users, category: "Business" },
  { id: "boardroom", label: "Boardroom", icon: Building2, category: "Business" },
  { id: "reception_area", label: "Reception", icon: Building2, category: "Business" },
  { id: "coworking_space", label: "Coworking", icon: Users, category: "Business" },
  
  // Retail/Commercial
  { id: "retail_store", label: "Retail Store", icon: Store, category: "Retail" },
  { id: "car_dealership", label: "Car Dealership", icon: Car, category: "Retail" },
  { id: "trade_show", label: "Trade Show", icon: Package, category: "Retail" },
  
  // Travel/Transport
  { id: "airport_terminal", label: "Airport Terminal", icon: Building2, category: "Travel" },
  { id: "hotel_lobby", label: "Hotel Lobby", icon: Building2, category: "Travel" },
  { id: "train_station", label: "Train Station", icon: Building2, category: "Travel" },
  { id: "beach_resort", label: "Beach Resort", icon: Sun, category: "Travel" },
  { id: "cruise_ship", label: "Cruise Ship", icon: Sun, category: "Travel" },
  { id: "travel_lounge", label: "Travel Lounge", icon: Coffee, category: "Travel" },
  
  // Media/Entertainment
  { id: "salon", label: "Salon", icon: Sparkles, category: "Media" },
  { id: "podcast_studio", label: "Podcast Studio", icon: Mic, category: "Media" },
  { id: "news_studio", label: "News Studio", icon: Video, category: "Media" },
  { id: "studio", label: "Studio", icon: Camera, category: "Media" },
  
  // Hospitality
  { id: "restaurant", label: "Restaurant", icon: Coffee, category: "Hospitality" },
  { id: "cafe", label: "Café", icon: Coffee, category: "Hospitality" },
  
  // Specialty
  { id: "medical_clinic", label: "Medical Clinic", icon: Heart, category: "Specialty" },
  { id: "real_estate", label: "Property Interior", icon: Home, category: "Specialty" },
  { id: "rooftop_terrace", label: "Rooftop Terrace", icon: Sun, category: "Specialty" },
  { id: "luxury_penthouse", label: "Luxury Penthouse", icon: Star, category: "Specialty" },
  { id: "factory_floor", label: "Factory", icon: Package, category: "Specialty" },
  { id: "fitness_center", label: "Fitness Center", icon: Dumbbell, category: "Specialty" },
];

const MOTION_STYLES = [
  { id: "selfie", label: "Selfie", icon: Camera },
  { id: "product_reveal", label: "Reveal", icon: Package },
  { id: "pov", label: "POV", icon: Eye },
];

const AGE_GROUPS = [
  { id: "young", label: "18-25" },
  { id: "adult", label: "25-40" },
  { id: "mature", label: "40+" },
];

const GENDERS = [
  { id: "female", label: "Female" },
  { id: "male", label: "Male" },
  { id: "neutral", label: "Neutral" },
];

const EMOTIONS = [
  { id: "excited", label: "Excited" },
  { id: "calm", label: "Calm" },
  { id: "surprised", label: "Surprised" },
  { id: "skeptical_convinced", label: "Skeptical→Convinced" },
];

const VIDEO_DURATIONS = [15, 30]; // O3 supports up to 15s native, 30s = 2x15s chained

const GALLERY_OPTIONS = [
  { count: 0, label: "None" },
  { count: 1, label: "1 Image" },
  { count: 3, label: "3 Images" },
  { count: 5, label: "5 Images" },
];

// Selection Button Component
const SelectBtn = ({ selected, onClick, children, className = "", ...props }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
      selected
        ? "bg-cyan-500/20 border-cyan-500 text-cyan-400 border"
        : "bg-white/5 border-white/10 text-white/70 border hover:bg-white/10 hover:text-white"
    } ${className}`}
    {...props}
  >
    {children}
  </button>
);

export default function UGCStudioPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  
  // Form restoration state
  const [formRestored, setFormRestored] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    persona: "american",
    ageGroup: "adult",
    gender: "female",
    emotion: "excited",
    scene: "kitchen",
    motionStyle: "selfie",
    videoDuration: 15,
    videoStyle: "all_motion",
    galleryOutro: 0, // 0, 1, 3, or 5 product images at the end
    presenterImage: null,
    presenterImagePreview: null,
    productImages: [], // Array of product image files for element binding
    productImagePreviews: [], // Array of preview URLs
    showFirstImage: false, // user picked the first photo as the colour/version to show
    mustShow: "",
    logoImage: null,
    skipOutfitPreview: false,
    productName: "",
    productDescription: "",
    productUrl: "",
    script: "",
    hooks: [],
    selectedHook: 0,
    enableCaptions: false,
    captionStyle: "classic", // classic, karaoke, boxed, bold_impact
    enableLipSync: true,
    // Video mode: "talking_head" | "presenter_voiceover" | "product_only" | "large_product"
    videoMode: "talking_head",
    // Background mode: "original" keeps presenter's original background, "scene" replaces with selected scene
    backgroundMode: "original",
    // Virtual Try-On: Make presenter wear the product (for clothing/fashion)
    enableVirtualTryon: false,
    // Virtual Try-On Product Type: helps AI focus on the right area
    tryonProductType: "clothing", // "clothing" | "shoes" | "jewelry" | "wig" | "accessories"
    secondItem: null, // optional second try-on item {type, name, images: File[]}
    // Large Product Style: "cinematic" (orbit only) | "presenter" (presenter interacting with product)
    largeProductStyle: "cinematic",
    // Large Product Category: determines interaction style
    largeProductCategory: "car", // "car" | "furniture" | "property" | "appliance" | "gym" | "outdoor"
    ugcQuality: "premium",
  });
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Reset form to create a new video
  const resetForm = () => {
    setForm({
      persona: "american",
      ageGroup: "adult",
      gender: "female",
      emotion: "excited",
      scene: "kitchen",
      motionStyle: "selfie",
      videoDuration: 15,
      videoStyle: "all_motion",
      galleryOutro: 0,
      presenterImage: null,
      presenterImagePreview: null,
      productImages: [],
      productImagePreviews: [],
      showFirstImage: false,
      mustShow: "",
      logoImage: null,
      skipOutfitPreview: form.skipOutfitPreview,
      productName: "",
      productDescription: "",
      productUrl: "",
      script: "",
      hooks: [],
      selectedHook: 0,
      enableCaptions: false,
      captionStyle: "classic",
      enableLipSync: true,
      videoMode: "talking_head",
      backgroundMode: "original",
      enableVirtualTryon: false,
      tryonProductType: "clothing",
      secondItem: null,
      largeProductStyle: "cinematic",
      largeProductCategory: "car",
      ugcQuality: form.ugcQuality,
    });
    setStep(1);
    setProgress(0);
    setStatus("");
    setVideoId(null);
    setVideoUrl(null);
    setIsGenerating(false);
  };
  
  // Current step (1-4)
  const [step, setStep] = useState(1);
  
  // Upgrade modal state for subscription quota errors
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeErrorType, setUpgradeErrorType] = useState(null);
  const [upgradeErrorData, setUpgradeErrorData] = useState(null);
  
  // A/B Testing modal state
  const [abTestModalOpen, setAbTestModalOpen] = useState(false);
  
  // Ghost Mode state
  const [ghostModeEnabled, setGhostModeEnabled] = useState(false);
  const [ghostProfile, setGhostProfile] = useState(null);
  
  // Cloned Voice state
  const [useClonedVoice, setUseClonedVoice] = useState(false);
  const [clonedVoices, setClonedVoices] = useState([]);
  const [selectedClonedVoice, setSelectedClonedVoice] = useState(null);
  
  // African Language Voice state (Khaya AI)
  const [africanLanguages, setAfricanLanguages] = useState([]);
  const [useAfricanLanguage, setUseAfricanLanguage] = useState(false);
  const [selectedAfricanLanguage, setSelectedAfricanLanguage] = useState(null);
  const [favoriteLanguages, setFavoriteLanguages] = useState([]);
  const [playingVoicePreview, setPlayingVoicePreview] = useState(null);
  const [loadingVoicePreview, setLoadingVoicePreview] = useState(null);
  const voicePreviewAudioRef = useRef(null);
  
  // Stock Footage state
  const [stockFootageEnabled, setStockFootageEnabled] = useState(false);
  const [stockFootageSettings, setStockFootageSettings] = useState(null);
  
  const [qualityOptions, setQualityOptions] = useState(null);
  const isStandard = form.ugcQuality === "standard";
  
  // Script word count warning dialog state
  
  // Template Preview Gallery state
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  
  // Update form helper
  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  
  // Live script-length check against the exact budget the UGC pipeline uses
  const fitRequest = {
    script: form.script,
    hook: form.hooks[form.selectedHook] || "",
    emotion: form.emotion,
    videoMode: form.videoMode,
    largeProductStyle: form.largeProductStyle,
    videoDuration: form.videoDuration,
    productName: form.productName,
    quality: form.ugcQuality,
  };
  const { fit: scriptFit, checking: checkingFit, checkNow: checkScriptFitNow } = useScriptFit(fitRequest);
  const [fitPanelOpen, setFitPanelOpen] = useState(false);
  const scriptInputRef = useRef(null);
  
  // Handle template selection from gallery
  const handleSelectTemplate = (templateData) => {
    setForm(prev => ({
      ...prev,
      persona: templateData.persona || prev.persona,
      gender: templateData.gender || prev.gender,
      emotion: templateData.emotion || prev.emotion,
      ageGroup: templateData.ageGroup || prev.ageGroup,
      videoMode: templateData.videoMode || prev.videoMode,
      largeProductCategory: templateData.largeProductCategory || prev.largeProductCategory,
    }));
    
    // If template has sample script, optionally show it
    if (templateData.sampleScript) {
      toast.success(`Template applied! Sample script style: "${templateData.sampleScript.substring(0, 50)}..."`);
    } else {
      toast.success("Template applied! Customize your settings and generate.");
    }
  };

  // Check for addon purchase success and restore form data
  useEffect(() => {
    const addonSuccess = searchParams.get("addon_success");
    
    if (addonSuccess === "true" && !formRestored) {
      const savedState = localStorage.getItem("addon_checkout_state");
      
      if (savedState) {
        try {
          const { formData: savedFormData, timestamp } = JSON.parse(savedState);
          const thirtyMinutes = 30 * 60 * 1000;
          
          if (Date.now() - timestamp < thirtyMinutes && savedFormData) {
            setForm(prev => ({ ...prev, ...savedFormData }));
            setFormRestored(true);
            toast.success("Add-on purchased! Your settings have been restored.");
            localStorage.removeItem("addon_checkout_state");
          }
        } catch (error) {
          console.error("Failed to restore form data:", error);
        }
      } else {
        toast.success("Add-on purchased successfully!");
      }
      
      // Clean up URL params
      searchParams.delete("addon_success");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, formRestored, setSearchParams]);
  
  // Standard (H3) = talking presenter, 15s; Premium (Kling) = everything
  const selectQuality = (quality) => setForm(prev => (quality === "standard"
    ? { ...prev, ugcQuality: quality, videoMode: "talking_head", videoDuration: prev.videoDuration >= 30 ? 30 : 15 }
    : { ...prev, ugcQuality: quality }));

  const pickMode = (mode) => {
    if (isStandard && mode !== "talking_head") {
      toast.info("Standard quality makes talking-presenter ads. Switch to Premium for this style.");
      return;
    }
    updateForm("videoMode", mode);
  };

  useEffect(() => {
    ugcAPI.getQualityOptions()
      .then((opts) => { setQualityOptions(opts); selectQuality(opts.default); })
      .catch(() => setQualityOptions(null))
      .then(() => (searchParams.get("restore") === "1" ? takeUgcDraft() : null))
      .then((draft) => {
        if (!draft) return;
        setForm(prev => ({ ...prev, ...draft.form }));
        setStep(draft.step);
        toast.success("Welcome back! Your ad settings are just as you left them.");
      })
      .catch(() => toast.error("Couldn't restore your ad settings."))
      .finally(() => {
        if (searchParams.get("restore")) {
          searchParams.delete("restore");
          setSearchParams(searchParams, { replace: true });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  
  // Handle presenter image upload
  const handlePresenterImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && file.type.includes("image")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateForm("presenterImage", file);
        updateForm("presenterImagePreview", e.target.result);
        toast.success("Presenter image uploaded!");
      };
      reader.readAsDataURL(file);
    }
  }, []);
  
  // Handle product image upload (supports multiple images)
  const handleProductImageUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Limit to 5 images max
    const validFiles = files.filter(f => f.type.includes("image")).slice(0, 5 - form.productImages.length);
    
    if (validFiles.length === 0) {
      toast.error("Please select valid image files");
      return;
    }
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setForm(prev => ({
          ...prev,
          productImages: [...prev.productImages, file].slice(0, 5),
          productImagePreviews: [...prev.productImagePreviews, e.target.result].slice(0, 5),
        }));
      };
      reader.readAsDataURL(file);
    });
    
    toast.success(`${validFiles.length} image(s) added for product reference`);
  }, [form.productImages.length]);
  
  // Handle selecting image from gallery picker or Media Library
  const handleGalleryImageSelect = useCallback((imageData) => {
    if (form.productImages.length >= 5) {
      toast.error("Maximum 5 product images allowed");
      return;
    }
    
    // Handle MediaPicker selection (has url property)
    if (imageData.url || imageData.thumbnail_url) {
      const imageUrl = imageData.url || imageData.thumbnail_url;
      // Fetch the image and add it
      fetch(imageUrl)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], imageData.name || "media-library-image.jpg", { type: blob.type || "image/jpeg" });
          const reader = new FileReader();
          reader.onloadend = () => {
            setForm(prev => ({
              ...prev,
              productImages: [...prev.productImages, file].slice(0, 5),
              productImagePreviews: [...prev.productImagePreviews, reader.result].slice(0, 5),
            }));
            toast.success("Image added from Media Library");
          };
          reader.readAsDataURL(blob);
        })
        .catch(err => {
          console.error("Failed to fetch image from Media Library:", err);
          toast.error("Failed to add image from Media Library");
        });
      return;
    }
    
    // Handle old gallery picker format (has file and preview)
    if (imageData.file && imageData.preview) {
      setForm(prev => ({
        ...prev,
        productImages: [...prev.productImages, imageData.file].slice(0, 5),
        productImagePreviews: [...prev.productImagePreviews, imageData.preview].slice(0, 5),
      }));
      toast.success("Image added from gallery");
      return;
    }
    
    // Handle direct file upload from MediaPicker
    if (imageData.isUpload && imageData.file) {
      const file = imageData.file;
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm(prev => ({
          ...prev,
          productImages: [...prev.productImages, file].slice(0, 5),
          productImagePreviews: [...prev.productImagePreviews, reader.result].slice(0, 5),
        }));
        toast.success("Image uploaded successfully");
      };
      reader.readAsDataURL(file);
    }
  }, [form.productImages.length]);
  
  // Remove a product image
  const removeProductImage = useCallback((index) => {
    setForm(prev => ({
      ...prev,
      productImages: prev.productImages.filter((_, i) => i !== index),
      productImagePreviews: prev.productImagePreviews.filter((_, i) => i !== index),
      showFirstImage: prev.showFirstImage && index !== 0,
    }));
  }, []);

  // Move the tapped photo to the front: that colour/version is the one the presenter shows
  const showProductImage = useCallback((index) => {
    const toFront = (list) => [list[index], ...list.filter((_, i) => i !== index)];
    setForm(prev => ({
      ...prev,
      productImages: toFront(prev.productImages),
      productImagePreviews: toFront(prev.productImagePreviews),
      showFirstImage: true,
    }));
  }, []);
  
  // Fetch user's cloned voices
  useEffect(() => {
    const fetchClonedVoices = async () => {
      try {
        const response = await voiceAPI.getMyVoices();
        setClonedVoices(response.data.voices || []);
        if (response.data.voices?.length > 0) {
          setSelectedClonedVoice(response.data.voices[0].voice_id);
        }
      } catch (error) {
        console.error("Failed to fetch cloned voices:", error);
      }
    };
    fetchClonedVoices();
  }, []);
  
  // Fetch African languages (Khaya AI)
  useEffect(() => {
    const fetchAfricanLanguages = async () => {
      try {
        const response = await voiceAPI.getAfricanLanguages();
        if (response.data.success && response.data.flat_list) {
          setAfricanLanguages(response.data.flat_list);
          if (response.data.flat_list.length > 0) {
            setSelectedAfricanLanguage(response.data.flat_list[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch African languages:", error);
      }
    };
    fetchAfricanLanguages();
  }, []);
  
  // Fetch favorite African languages
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await voiceAPI.getAfricanFavorites();
        if (response.data.success) {
          setFavoriteLanguages(response.data.favorites || []);
        }
      } catch (error) {
        console.error("Failed to fetch favorite languages:", error);
      }
    };
    fetchFavorites();
  }, []);
  
  // Sort African languages with favorites first
  const sortedAfricanLanguages = [...africanLanguages].sort((a, b) => {
    const aIsFav = favoriteLanguages.includes(a.id);
    const bIsFav = favoriteLanguages.includes(b.id);
    if (aIsFav && !bIsFav) return -1;
    if (!aIsFav && bIsFav) return 1;
    return 0;
  });
  
  // Toggle favorite language
  const toggleFavoriteLanguage = async (voiceId) => {
    const isFav = favoriteLanguages.includes(voiceId);
    try {
      if (isFav) {
        await voiceAPI.removeAfricanFavorite(voiceId);
        setFavoriteLanguages(prev => prev.filter(id => id !== voiceId));
        toast.success("Removed from favorites");
      } else {
        await voiceAPI.addAfricanFavorite(voiceId);
        setFavoriteLanguages(prev => [...prev, voiceId]);
        toast.success("Added to favorites");
      }
    } catch (error) {
      console.error("Failed to update favorite:", error);
      toast.error("Failed to update favorite");
    }
  };
  
  // Play voice preview sample
  const playVoicePreview = async (voiceId) => {
    // Stop current preview if playing
    if (voicePreviewAudioRef.current) {
      voicePreviewAudioRef.current.pause();
      voicePreviewAudioRef.current = null;
    }
    
    if (playingVoicePreview === voiceId) {
      setPlayingVoicePreview(null);
      return;
    }
    
    setLoadingVoicePreview(voiceId);
    
    try {
      const response = await voiceAPI.getAfricanVoiceSample(voiceId);
      
      if (response.data.success) {
        let audioUrl;
        
        if (response.data.audio_url) {
          // Cached URL from S3
          audioUrl = response.data.audio_url;
        } else if (response.data.audio_data) {
          // Base64 encoded audio
          audioUrl = `data:${response.data.audio_type || 'audio/mp3'};base64,${response.data.audio_data}`;
        } else {
          throw new Error("No audio data in response");
        }
        
        const audio = new Audio(audioUrl);
        voicePreviewAudioRef.current = audio;
        
        audio.onended = () => {
          setPlayingVoicePreview(null);
          voicePreviewAudioRef.current = null;
        };
        
        audio.onerror = () => {
          toast.error("Failed to play audio sample");
          setPlayingVoicePreview(null);
          voicePreviewAudioRef.current = null;
        };
        
        await audio.play();
        setPlayingVoicePreview(voiceId);
      } else {
        throw new Error(response.data.error || "Failed to get voice sample");
      }
    } catch (error) {
      console.error("Failed to play voice preview:", error);
      toast.error("Voice preview not available");
    } finally {
      setLoadingVoicePreview(null);
    }
  };
  
  // Handle incoming product image from Image Studio
  useEffect(() => {
    if (location.state?.productImage) {
      const imageData = location.state.productImage;
      if (imageData.url) {
        // Fetch the image and add it as a product image
        fetch(imageData.url)
          .then(res => res.blob())
          .then(blob => {
            const file = new File([blob], `product_${Date.now()}.png`, { type: blob.type });
            setForm(prev => ({
              ...prev,
              productImages: [...prev.productImages, file].slice(0, 5),
              productImagePreviews: [...prev.productImagePreviews, imageData.url].slice(0, 5),
            }));
            toast.success("Product image loaded from Image Studio");
          })
          .catch(err => {
            console.error("Failed to load product image:", err);
          });
      }
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Fetch product info from URL
  const fetchProductInfo = async () => {
    if (!form.productUrl.trim()) return;
    try {
      new URL(form.productUrl);
    } catch { toast.error("Invalid URL"); return; }
    
    toast.loading("Fetching product info...");
    try {
      const response = await ugcAPI.scrapeProductUrl(form.productUrl);
      
      // Check if scraping was successful
      if (response.success === false || response.error) {
        toast.dismiss();
        toast.error(response.error || "Could not fetch product info. Please enter details manually.");
        return;
      }
      
      // Only update fields if we got actual data
      const hasName = response.name && response.name.trim();
      const hasDescription = response.description && response.description.trim();
      
      if (!hasName && !hasDescription) {
        toast.dismiss();
        toast.error("No product info found. Please enter details manually.");
        return;
      }
      
      setForm(prev => ({
        ...prev,
        productName: prev.productName || response.name || "",
        productDescription: prev.productDescription || response.description || "",
      }));
      
      // Also try to fetch images if available
      if (response.images && response.images.length > 0) {
        // Add images to product images
        const newPreviews = response.images.slice(0, 5 - form.productImagePreviews.length);
        if (newPreviews.length > 0) {
          setForm(prev => ({
            ...prev,
            productImagePreviews: [...prev.productImagePreviews, ...newPreviews],
          }));
        }
      }
      
      toast.dismiss();
      toast.success("Product info fetched!");
    } catch (error) {
      toast.dismiss();
      console.error("Product fetch error:", error);
      toast.error("Failed to fetch product info. Please enter details manually.");
    }
  };

  // Analyze product screenshot
  const analyzeProductScreenshot = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 10MB.");
      return;
    }
    
    toast.loading("Analyzing product screenshot...");
    try {
      const response = await ugcAPI.analyzeProductImage(file);
      
      if (response.success === false || response.error) {
        toast.dismiss();
        toast.error(response.error || "Could not analyze image. Please enter details manually.");
        return;
      }
      
      // Update form with extracted data
      setForm(prev => ({
        ...prev,
        productName: prev.productName || response.name || "",
        productDescription: prev.productDescription || response.description || "",
      }));
      
      toast.dismiss();
      
      // Show detailed success message
      const extractedInfo = [];
      if (response.name) extractedInfo.push(`Name: ${response.name}`);
      if (response.price) extractedInfo.push(`Price: ${response.price}`);
      if (response.brand) extractedInfo.push(`Brand: ${response.brand}`);
      
      toast.success(
        <div>
          <p className="font-semibold">Product info extracted!</p>
          {response.features && response.features.length > 0 && (
            <p className="text-sm text-white/70 mt-1">
              Found {response.features.length} features
            </p>
          )}
        </div>
      );
    } catch (error) {
      toast.dismiss();
      console.error("Screenshot analysis error:", error);
      toast.error("Failed to analyze screenshot. Please enter details manually.");
    }
  };
  
  // Generate AI script
  const generateScript = async () => {
    if (!form.productName) {
      toast.error("Enter product name first");
      return;
    }
    toast.loading("Generating script...");
    try {
      console.log("[UGC] Generating script for:", form.productName);
      const response = await ugcAPI.generateScript({
        productName: form.productName,
        productDescription: form.productDescription,
        productUrl: form.productUrl,
        persona: form.persona,
        scene: form.scene,
        emotion: form.emotion,
        ageGroup: form.ageGroup,
        videoDuration: form.videoDuration,
      });
      console.log("[UGC] Script response:", response);
      
      if (response.script) {
        updateForm("script", response.script);
        updateForm("hooks", response.hooks || []);
        toast.dismiss();
        if (response.error) {
          // Got a fallback script with warning
          toast.success("Script generated (using template)");
        } else {
          toast.success("Script generated!");
        }
      } else {
        console.error("[UGC] No script in response:", response);
        toast.dismiss();
        toast.error("No script received from server");
      }
    } catch (err) {
      console.error("[UGC] Script generation error:", err);
      toast.dismiss();
      // Show more detailed error
      const errorMsg = err?.response?.data?.detail || err?.message || "Failed to generate script";
      toast.error(errorMsg);
    }
  };
  
  // Generate hooks only
  const generateHooks = async () => {
    toast.loading("Generating viral hooks...");
    try {
      const response = await ugcAPI.generateHooks({
        productName: form.productName,
        productDescription: form.productDescription,
        persona: form.persona,
      });
      updateForm("hooks", response.hooks || []);
      toast.dismiss();
      toast.success("Hooks generated!");
    } catch {
      toast.dismiss();
      toast.error("Failed to generate hooks");
    }
  };
  
  // Poll for video progress
  useEffect(() => {
    if (!videoId || !isGenerating) return;
    
    let pollInterval = null;
    
    // Initial delay before first poll to ensure DB record is written
    const initialDelay = setTimeout(() => {
      pollInterval = setInterval(async () => {
        try {
          const response = await videosAPI.getVideoProgress(videoId);
          const data = response.data;
          
          if (data.progress_percent) setProgress(data.progress_percent);
          if (data.message || data.progress) setStatus(data.message || data.progress);
          
          // Check if Khaya African language service is unavailable
          if (data.status === "khaya_unavailable") {
            clearInterval(pollInterval);
            setIsGenerating(false);
            
            // Show confirmation dialog to user
            const userConfirmed = window.confirm(
              "African language voiceover is temporarily unavailable.\n\n" +
              "Would you like to proceed with English voiceover instead?\n\n" +
              "Click 'OK' to continue with English, or 'Cancel' to try again later."
            );
            
            if (userConfirmed) {
              // User wants to proceed with English fallback - retry with forceEnglishFallback
              toast.info("Retrying with English voiceover...");
              retryWithEnglishFallback(data.id);
            } else {
              // User declined - show message and return to form
              toast.info("Video generation cancelled. You can try again later when African languages are available.");
              setVideoId(null);
            }
            return;
          }
          
          if (data.status === "pending" || data.status === "completed") {
            clearInterval(pollInterval);
            setVideoUrl(data.video_url || data.cloud_video_url);
            setProgress(100);
            setStatus("Video ready!");
            setIsGenerating(false);
            toast.success("Your UGC ad is ready!");
          } else if (data.status === "failed") {
            // Only show failure if progress is 0 or error is explicitly set
            // This prevents false "failed" messages during brief race conditions
            if (data.progress_percent === 0 || data.error) {
              clearInterval(pollInterval);
              setIsGenerating(false);
              toast.error(data.error || "Generation failed");
            } else {
              // Status might be temporarily inconsistent, keep polling
              console.log("Ignoring transient failed status, continuing to poll...");
            }
          }
        } catch (err) {
          // Don't show error for 404 - video might not be in DB yet
          if (err.response?.status !== 404) {
            console.error("Poll error:", err);
          }
        }
      }, 8000); // Poll every 8 seconds
    }, 2000); // Wait 2 seconds before starting to poll
    
    return () => {
      clearTimeout(initialDelay);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [videoId, isGenerating]);
  
  // Retry video generation with English fallback when African language is unavailable
  const retryWithEnglishFallback = async (failedVideoId) => {
    setIsGenerating(true);
    setProgress(5);
    setStatus("Retrying with English voiceover...");
    
    try {
      const response = await ugcAPI.generateAd({
        persona: form.persona,
        ageGroup: form.ageGroup,
        gender: form.gender,
        emotion: form.emotion,
        scene: form.scene,
        motionStyle: form.motionStyle,
        videoDuration: form.videoDuration,
        videoStyle: form.videoStyle,
        galleryOutro: form.galleryOutro,
        productName: form.productName,
        productDescription: form.productDescription,
        presenterImage: form.presenterImage,
        productImages: form.productImages,
        script: form.script,
        hook: form.hooks[form.selectedHook] || "",
        enableCaptions: form.enableCaptions,
        enableLipSync: form.enableLipSync,
        videoMode: form.videoMode,
        backgroundMode: form.backgroundMode,
        enableVirtualTryon: form.enableVirtualTryon,
        tryonProductType: form.enableVirtualTryon ? form.tryonProductType : null,
        largeProductStyle: form.videoMode === "large_product" ? form.largeProductStyle : null,
        largeProductCategory: form.videoMode === "large_product" && form.largeProductStyle === "presenter" ? form.largeProductCategory : null,
        useClonedVoice: false,  // Don't use cloned voice for fallback
        clonedVoiceId: null,
        // Keep African language selection but force English fallback
        useAfricanLanguage: useAfricanLanguage,
        africanLanguageId: selectedAfricanLanguage,
        forceEnglishFallback: true,  // KEY: Force English voiceover
        ugcQuality: form.ugcQuality,
        mustShow: form.mustShow.trim(),
        showFirstImage: form.showFirstImage && form.productImages.length > 1,
        logoImage: form.logoImage,
      });
      
      if (response.videoId) {
        setVideoId(response.videoId);
        setStatus("Video generation restarted with English voiceover...");
        setProgress(10);
        toast.success("Retrying with English voiceover!");
      }
    } catch (error) {
      console.error('Retry error:', error);
      setIsGenerating(false);
      toast.error("Failed to retry. Please try again.");
    }
  };
  
  // Generate video: instant fit check first, options panel only when the script is too long
  const generateVideo = async () => {
    if (!form.productName) {
      toast.error("Enter product name");
      return;
    }
    if (!(stockFootageEnabled && stockFootageSettings?.enabled) && form.script?.trim()) {
      try {
        const result = await checkScriptFitNow();
        if (!result.fits) {
          setFitPanelOpen(true);
          return;
        }
      } catch {
        // Fit check is advisory - never block generation on it
      }
    }
    generateVideoConfirmed();
  };
  
  const handleFitPanelGenerate = (overrides) => {
    setFitPanelOpen(false);
    if (overrides.applyScript) {
      updateForm("script", overrides.script);
      updateForm("selectedHook", -1);
    }
    if (overrides.videoDuration) updateForm("videoDuration", overrides.videoDuration);
    generateVideoConfirmed(overrides);
  };
  
  // Script box lives on step 3; focus it as soon as it mounts (after the step transition)
  const focusScriptPending = useRef(false);
  const attachScriptInput = (el) => {
    scriptInputRef.current = el;
    if (el && focusScriptPending.current) {
      focusScriptPending.current = false;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  const focusScriptInput = () => {
    if (step === 3 && scriptInputRef.current) {
      scriptInputRef.current.focus();
      scriptInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    focusScriptPending.current = true;
    setStep(3);
  };
  
  // Same payload for single ads and A/B variations; overrides replace form values
  const buildAdPayload = (overrides = {}) => ({
    persona: form.persona,
    ageGroup: form.ageGroup,
    gender: form.gender,
    emotion: form.emotion,
    scene: form.scene,
    motionStyle: form.motionStyle,
    videoDuration: form.videoDuration,
    videoStyle: form.videoStyle,
    galleryOutro: form.galleryOutro,
    productName: form.productName,
    productDescription: form.productDescription,
    presenterImage: form.presenterImage,
    productImages: form.productImages,
    script: form.script,
    hook: form.hooks[form.selectedHook] || "",
    enableCaptions: form.enableCaptions,
    enableLipSync: form.enableLipSync,
    videoMode: form.videoMode,
    backgroundMode: form.backgroundMode,
    enableVirtualTryon: form.enableVirtualTryon,
    tryonProductType: form.enableVirtualTryon ? form.tryonProductType : null,
    secondItem: form.enableVirtualTryon && form.secondItem?.images?.length ? form.secondItem : null,
    largeProductStyle: form.videoMode === "large_product" ? form.largeProductStyle : null,
    largeProductCategory: form.videoMode === "large_product" && form.largeProductStyle === "presenter" ? form.largeProductCategory : null,
    // Talking Head uses Kling O3 native TTS, so cloned/African voices only apply to voiceover modes
    useClonedVoice: form.videoMode !== "talking_head" && useClonedVoice && clonedVoices.length > 0,
    clonedVoiceId: form.videoMode !== "talking_head" && useClonedVoice && selectedClonedVoice ? selectedClonedVoice : null,
    useAfricanLanguage: form.videoMode !== "talking_head" && useAfricanLanguage && africanLanguages.length > 0,
    africanLanguageId: form.videoMode !== "talking_head" && useAfricanLanguage && selectedAfricanLanguage ? selectedAfricanLanguage : null,
    ugcQuality: form.ugcQuality,
    mustShow: form.mustShow.trim(),
    showFirstImage: form.showFirstImage && form.productImages.length > 1,
    logoImage: form.logoImage,
    ...Object.fromEntries(Object.entries(overrides).filter(([k]) => k !== "applyScript")),
  });
  
  // Clothing/shoes try-on: approve one outfit photo first (unless skipped); the video starts from it
  const [outfitPending, setOutfitPending] = useState(null);
  const generateVideoConfirmed = (overrides = {}) => {
    const needsOutfitCheck = form.enableVirtualTryon && OUTFIT_PREVIEW_TYPES.includes(form.tryonProductType)
      && form.presenterImage && form.productImages.length > 0 && !form.skipOutfitPreview && !overrides.outfitPreviewId;
    if (needsOutfitCheck) {
      setOutfitPending(overrides);
      return;
    }
    runVideoGeneration(overrides);
  };

  // Actual video generation (called after validation passes or user confirms)
  const runVideoGeneration = async (overrides = {}) => {
    
    setIsGenerating(true);
    setProgress(5);
    setStatus("Initializing...");
    setVideoUrl(null);
    
    try {
      // =====================================================
      // STOCK FOOTAGE VIDEO GENERATION (Automatic Mode)
      // =====================================================
      if (stockFootageEnabled && stockFootageSettings?.enabled) {
        // Generate script if not done
        if (!form.script) {
          setStatus("Generating script...");
          setProgress(10);
          await generateScript();
        }
        
        setStatus("AI is finding matching stock footage...");
        setProgress(20);
        
        // Call stock footage video generation API with auto_mode
        const response = await stockFootageAPI.generateVideo({
          title: `${form.productName} Ad`,
          script: form.script,
          stock_clips: [], // Empty - auto mode will find clips
          auto_mode: true, // Automatic footage selection
          voice_tier: stockFootageSettings.voiceTier || "free",
          voice_id: stockFootageSettings.voiceId || null,
          hashtags: []
        });
        
        if (response.data?.video_id) {
          setVideoId(response.data.video_id);
        }
        
        const voiceTierName = {
          "none": "No Voice",
          "free": "Free Voice (Edge TTS)",
          "premium": "Premium Voice"
        }[stockFootageSettings.voiceTier] || "Free Voice";
        
        setStatus(`Stock footage video started with ${voiceTierName}!`);
        setProgress(30);
        toast.success(`Stock Footage video generation started!`, {
          description: `AI is finding matching visuals with ${voiceTierName}.`,
          duration: 5000
        });
        return;
      }
      
      // Generate script if not done
      if (!form.script) {
        setStatus("Generating script...");
        setProgress(10);
        await generateScript();
      }
      
      // Generate hooks if not done
      if (form.hooks.length === 0) {
        setStatus("Creating viral hooks...");
        setProgress(15);
        await generateHooks();
      }
      
      setStatus("Starting video generation...");
      setProgress(20);
      
      const response = await ugcAPI.generateAd(buildAdPayload(overrides));
      
      if (response.videoId) {
        setVideoId(response.videoId);
        setStatus(response.message || "Video queued - starting shortly...");
        setProgress(25);
      }
    } catch (error) {
      console.error("Generation error:", error);
      
      // Check for subscription quota errors (403)
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail && typeof detail === 'object') {
          // Show upgrade modal for quota-related errors
          const errorType = detail.error || 'ugc_access_required';
          setUpgradeErrorType(errorType);
          setUpgradeErrorData(detail);
          setUpgradeModalOpen(true);
          setIsGenerating(false);
          return;
        }
      }
      
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else {
        toast.error("Generation failed. Please try again.");
      }
      setIsGenerating(false);
    }
  };
  
  // Video controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  // Download video
  const downloadVideo = () => {
    if (videoUrl) {
      const a = document.createElement("a");
      a.href = videoUrl;
      a.download = `ugc-ad-${form.productName.replace(/\s+/g, "-")}.mp4`;
      a.click();
      toast.success("Download started!");
    }
  };
  
  // Step navigation
  const nextStep = () => step < 4 && setStep(step + 1);
  const prevStep = () => step > 1 && setStep(step - 1);
  
  // Check if step is complete
  const isStepComplete = (s) => {
    switch (s) {
      case 1: return true; // Persona always has defaults
      case 2: return true; // Scene always has default
      case 3: return form.productName.trim() !== "";
      case 4: return true;
      default: return false;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0A0A0B] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-cyan-400" />
          UGC Ad Studio
        </h1>
        <p className="text-white/50 text-sm mt-1">Create viral video ads in minutes</p>
      </div>
      
      <div className="max-w-4xl mx-auto">
        {/* Main Content - Full Width (Preview removed) */}
        <div>
          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-4">
            {[1, 2, 3, 4].map((s) => (
              <button
                key={s}
                onClick={() => setStep(s)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  step === s
                    ? "bg-cyan-500 text-black"
                    : isStepComplete(s)
                    ? "bg-white/10 text-white"
                    : "bg-white/5 text-white/40"
                }`}
              >
                {isStepComplete(s) && step !== s ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <span>{s}</span>
                )}
                <span className="hidden sm:inline">
                  {s === 1 && "Persona"}
                  {s === 2 && "Scene"}
                  {s === 3 && "Product"}
                  {s === 4 && "Generate"}
                </span>
              </button>
            ))}
          </div>
          
          {/* Step Content */}
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 md:p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Persona */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <UgcQualityPicker options={qualityOptions} value={form.ugcQuality}
                    onChange={selectQuality} onUpgrade={() => navigate("/pricing")} />

                  {/* Video Mode Selection - 3 Options */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-lg font-semibold text-white">Choose Ad Style</h2>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowTemplateGallery(true)}
                        className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-cyan-500/50 hover:border-cyan-400 text-cyan-400 hover:text-cyan-300"
                        data-testid="browse-templates-btn"
                      >
                        <LayoutGrid className="w-4 h-4 mr-2" />
                        Browse Templates
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-cyan-500/30 rounded">FREE</span>
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {/* Option 1: Talking Head Ad */}
                      <div
                        onClick={() => updateForm("videoMode", "talking_head")}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          form.videoMode === "talking_head"
                            ? "bg-cyan-500/10 border-cyan-500 shadow-lg shadow-cyan-500/20"
                            : "bg-white/5 border-white/10 hover:border-white/30"
                        }`}
                        data-testid="mode-talking-head"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            form.videoMode === "talking_head" ? "bg-cyan-500/20" : "bg-white/10"
                          }`}>
                            <User className={`w-6 h-6 ${form.videoMode === "talking_head" ? "text-cyan-400" : "text-white/60"}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className={`font-semibold ${form.videoMode === "talking_head" ? "text-cyan-400" : "text-white"}`}>
                                Talking Head Ad
                              </h3>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 font-medium">DEFAULT</span>
                            </div>
                            <p className="text-white/50 text-sm mt-1">
                              Presenter speaks directly to camera with lip-sync animation
                            </p>
                            <div className="flex gap-2 mt-2">
                              <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">Lip-Sync</span>
                              <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">Native Audio</span>
                            </div>
                          </div>
                          {form.videoMode === "talking_head" && (
                            <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>

                      {/* Option 2: Presenter with Voiceover */}
                      <div
                        onClick={() => pickMode("presenter_voiceover")}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          form.videoMode === "presenter_voiceover"
                            ? "bg-purple-500/10 border-purple-500 shadow-lg shadow-purple-500/20"
                            : "bg-white/5 border-white/10 hover:border-white/30"
                        }`}
                        data-testid="mode-presenter-voiceover"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            form.videoMode === "presenter_voiceover" ? "bg-purple-500/20" : "bg-white/10"
                          }`}>
                            <Mic className={`w-6 h-6 ${form.videoMode === "presenter_voiceover" ? "text-purple-400" : "text-white/60"}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-semibold ${form.videoMode === "presenter_voiceover" ? "text-purple-400" : "text-white"}`}>
                              Presenter with Voiceover
                            </h3>
                            <p className="text-white/50 text-sm mt-1">
                              Presenter showcases product while separate voiceover plays
                            </p>
                            <div className="flex gap-2 mt-2">
                              <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">No Lip-Sync</span>
                              <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">ElevenLabs Voice</span>
                            </div>
                          </div>
                          {form.videoMode === "presenter_voiceover" && (
                            <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>

                      {/* Option 3: Product-Only Ad */}
                      <div
                        onClick={() => pickMode("product_only")}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          form.videoMode === "product_only"
                            ? "bg-amber-500/10 border-amber-500 shadow-lg shadow-amber-500/20"
                            : "bg-white/5 border-white/10 hover:border-white/30"
                        }`}
                        data-testid="mode-product-only"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            form.videoMode === "product_only" ? "bg-amber-500/20" : "bg-white/10"
                          }`}>
                            <Package className={`w-6 h-6 ${form.videoMode === "product_only" ? "text-amber-400" : "text-white/60"}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-semibold ${form.videoMode === "product_only" ? "text-amber-400" : "text-white"}`}>
                              Product-Only Ad
                            </h3>
                            <p className="text-white/50 text-sm mt-1">
                              Cinematic product showcase with voiceover narration
                            </p>
                            <div className="flex gap-2 mt-2">
                              <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">No Presenter</span>
                              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">ElevenLabs Voice</span>
                            </div>
                          </div>
                          {form.videoMode === "product_only" && (
                            <Check className="w-5 h-5 text-amber-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>

                      {/* Option 4: Large Product Showcase (Cars, Furniture, Property) */}
                      <div
                        onClick={() => pickMode("large_product")}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          form.videoMode === "large_product"
                            ? "bg-emerald-500/10 border-emerald-500 shadow-lg shadow-emerald-500/20"
                            : "bg-white/5 border-white/10 hover:border-white/30"
                        }`}
                        data-testid="mode-large-product"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                            form.videoMode === "large_product" ? "bg-emerald-500/20" : "bg-white/10"
                          }`}>
                            <Car className={`w-6 h-6 ${form.videoMode === "large_product" ? "text-emerald-400" : "text-white/60"}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className={`font-semibold ${form.videoMode === "large_product" ? "text-emerald-400" : "text-white"}`}>
                                Large Product Showcase
                              </h3>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">NEW</span>
                            </div>
                            <p className="text-white/50 text-sm mt-1">
                              For cars, furniture, property & large items with cinematic motion
                            </p>
                            <div className="flex gap-2 mt-2">
                              <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">Image-to-Video</span>
                              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Cinematic Motion</span>
                              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Voiceover</span>
                            </div>
                          </div>
                          {form.videoMode === "large_product" && (
                            <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ghost Mode Panel - Viral Ghosting™ */}
                  <GhostModePanel
                    mode="ugc"
                    isEnabled={ghostModeEnabled}
                    onGhostModeToggle={setGhostModeEnabled}
                    onGhostProfileSelect={setGhostProfile}
                  />

                  {/* Stock Footage Toggle (Automatic Mode) */}
                  <StockFootageToggle
                    isEnabled={stockFootageEnabled}
                    onToggle={setStockFootageEnabled}
                    onSettingsChange={setStockFootageSettings}
                    clonedVoices={clonedVoices}
                  />

                  {/* Presenter & Voice Options - Show for talking_head and presenter_voiceover */}
                  {(form.videoMode === "talking_head" || form.videoMode === "presenter_voiceover") && (
                    <>
                      <h2 className="text-lg font-semibold text-white pt-2">Presenter Persona</h2>
                      
                      {/* Persona/Accent */}
                      <div>
                        <Label className="text-white/70 text-xs mb-2 block">Accent & Voice</Label>
                        <div className="flex flex-wrap gap-2">
                          {PERSONAS.map((p) => (
                            <SelectBtn
                              key={p.id}
                              selected={form.persona === p.id}
                              onClick={() => updateForm("persona", p.id)}
                            >
                              {p.flag} {p.label}
                            </SelectBtn>
                          ))}
                        </div>
                      </div>
                      
                      {/* Age Group */}
                      <div>
                        <Label className="text-white/70 text-xs mb-2 block">Age Group</Label>
                        <div className="flex gap-2">
                          {AGE_GROUPS.map((a) => (
                            <SelectBtn
                              key={a.id}
                              selected={form.ageGroup === a.id}
                              onClick={() => updateForm("ageGroup", a.id)}
                            >
                              {a.label}
                            </SelectBtn>
                          ))}
                        </div>
                      </div>
                      
                      {/* Gender */}
                      <div>
                        <Label className="text-white/70 text-xs mb-2 block">Gender</Label>
                        <div className="flex gap-2">
                          {GENDERS.map((g) => (
                            <SelectBtn
                              key={g.id}
                              selected={form.gender === g.id}
                              onClick={() => updateForm("gender", g.id)}
                            >
                              {g.label}
                            </SelectBtn>
                          ))}
                        </div>
                      </div>
                      
                      {/* Use My Voice Toggle - Only show for modes that support ElevenLabs voiceover */}
                      {clonedVoices.length > 0 && form.videoMode !== "talking_head" && (
                        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg p-4 border border-purple-500/30">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <Label className="text-white flex items-center gap-2">
                                <Mic className="w-4 h-4 text-purple-400" />
                                Use My Cloned Voice
                              </Label>
                              <p className="text-xs text-muted-foreground mt-1">
                                Use your cloned voice for the voiceover
                              </p>
                            </div>
                            <Switch
                              checked={useClonedVoice}
                              onCheckedChange={(checked) => {
                                setUseClonedVoice(checked);
                                if (checked) setUseAfricanLanguage(false); // Can't use both
                              }}
                              data-testid="use-cloned-voice-toggle"
                            />
                          </div>
                          
                          {useClonedVoice && (
                            <div className="space-y-2">
                              <Label className="text-white/70 text-xs">Select Voice</Label>
                              <div className="flex flex-wrap gap-2">
                                {clonedVoices.map((voice) => (
                                  <SelectBtn
                                    key={voice.voice_id}
                                    selected={selectedClonedVoice === voice.voice_id}
                                    onClick={() => setSelectedClonedVoice(voice.voice_id)}
                                  >
                                    🎙️ {voice.name}
                                  </SelectBtn>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* African Languages - Khaya AI - Only show for modes that support ElevenLabs voiceover */}
                      {africanLanguages.length > 0 && form.videoMode !== "talking_head" && (
                        <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg p-4 border border-emerald-500/30">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <Label className="text-white flex items-center gap-2">
                                <Globe className="w-4 h-4 text-emerald-400" />
                                Use African Language
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">NEW</span>
                              </Label>
                              <p className="text-xs text-muted-foreground mt-1">
                                Voiceover in Twi, Yoruba, Swahili & more African languages
                              </p>
                            </div>
                            <Switch
                              checked={useAfricanLanguage}
                              onCheckedChange={(checked) => {
                                setUseAfricanLanguage(checked);
                                if (checked) setUseClonedVoice(false); // Can't use both
                              }}
                              data-testid="use-african-language-toggle"
                            />
                          </div>
                          
                          {useAfricanLanguage && (
                            <div className="space-y-3">
                              {/* Favorites section header */}
                              {favoriteLanguages.length > 0 && (
                                <div className="flex items-center gap-2 text-xs text-amber-400/80">
                                  <Star className="w-3 h-3 fill-amber-400" />
                                  <span>Favorites shown first</span>
                                </div>
                              )}
                              
                              <Label className="text-white/70 text-xs">Select Language (click ⭐ to pin, 🔊 to preview)</Label>
                              <div className="flex flex-wrap gap-2">
                                {sortedAfricanLanguages.map((lang) => {
                                  const isFav = favoriteLanguages.includes(lang.id);
                                  const isPlaying = playingVoicePreview === lang.id;
                                  const isLoading = loadingVoicePreview === lang.id;
                                  
                                  return (
                                    <div 
                                      key={lang.id} 
                                      className={`relative group flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer ${
                                        selectedAfricanLanguage === lang.id
                                          ? "bg-cyan-500/30 text-cyan-300 border border-cyan-400/50"
                                          : "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:border-white/20"
                                      } ${isFav ? "ring-1 ring-amber-400/30" : ""}`}
                                      onClick={() => setSelectedAfricanLanguage(lang.id)}
                                      data-testid={`african-lang-${lang.id}`}
                                    >
                                      {/* Star (Favorite) button */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleFavoriteLanguage(lang.id);
                                        }}
                                        className={`p-0.5 rounded transition-colors ${
                                          isFav 
                                            ? "text-amber-400 hover:text-amber-300" 
                                            : "text-white/30 hover:text-amber-400/70"
                                        }`}
                                        title={isFav ? "Remove from favorites" : "Add to favorites"}
                                        data-testid={`fav-${lang.id}`}
                                      >
                                        <Star className={`w-3 h-3 ${isFav ? "fill-amber-400" : ""}`} />
                                      </button>
                                      
                                      {/* Language name */}
                                      <span className="mx-1">{lang.name}</span>
                                      
                                      {/* Play preview button - only show for TTS-supported languages */}
                                      {lang.tts_supported !== false ? (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            playVoicePreview(lang.id);
                                          }}
                                          className={`p-0.5 rounded transition-colors ${
                                            isPlaying 
                                              ? "text-emerald-400" 
                                              : "text-white/30 hover:text-emerald-400/70"
                                          }`}
                                          title="Preview voice"
                                          disabled={isLoading}
                                          data-testid={`preview-${lang.id}`}
                                        >
                                          {isLoading ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : isPlaying ? (
                                            <VolumeX className="w-3 h-3" />
                                          ) : (
                                            <Volume2 className="w-3 h-3" />
                                          )}
                                        </button>
                                      ) : (
                                        <span 
                                          className="text-white/20 text-[9px] ml-1" 
                                          title="TTS preview not available for this language"
                                        >
                                          (text only)
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Info message when Talking Head mode is selected - cloned voice not supported */}
                      {clonedVoices.length > 0 && form.videoMode === "talking_head" && (
                        <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                          <div className="flex items-start gap-3">
                            <Mic className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white text-sm font-medium">Cloned Voice Not Available</p>
                              <p className="text-white/60 text-xs mt-1">
                                Talking Head mode uses AI-generated lip-sync audio. To use your cloned voice, 
                                switch to <span className="text-purple-400 font-medium">"Presenter with Voiceover"</span> mode.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Emotion */}
                      <div>
                        <Label className="text-white/70 text-xs mb-2 block">Emotion Style</Label>
                        <div className="flex flex-wrap gap-2">
                          {EMOTIONS.map((e) => (
                            <SelectBtn
                              key={e.id}
                              selected={form.emotion === e.id}
                              onClick={() => updateForm("emotion", e.id)}
                            >
                              {e.label}
                            </SelectBtn>
                          ))}
                        </div>
                      </div>
                      
                      {/* Presenter Image Upload */}
                      <div>
                        <Label className="text-white/70 text-xs mb-2 block">Presenter Image (Optional - AI will generate if not provided)</Label>
                        <label
                          className={`flex items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer transition-all ${
                            form.presenterImagePreview
                              ? "border-cyan-500/50 bg-cyan-500/10"
                              : "border-white/20 hover:border-white/40 bg-white/5"
                          }`}
                        >
                          {form.presenterImagePreview ? (
                            <div className="relative w-full">
                              <img
                                src={form.presenterImagePreview}
                                alt="Presenter"
                                className="w-full max-h-64 rounded-lg object-contain"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  updateForm("presenterImage", null);
                                  updateForm("presenterImagePreview", null);
                                }}
                                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 rounded-full p-1.5 text-white shadow-lg"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <Upload className="w-8 h-8 mx-auto text-white/40 mb-2" />
                              <p className="text-white/60 text-sm">Upload presenter/character image</p>
                              <p className="text-white/40 text-xs mt-1">For best results, use a clear headshot</p>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePresenterImageUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </>
                  )}

                  {/* Voice Options - Show for product_only and large_product modes */}
                  {(form.videoMode === "product_only" || form.videoMode === "large_product") && (
                    <>
                      {/* Large Product Mode - Style Selector (show FIRST for large_product) */}
                      {form.videoMode === "large_product" && (
                        <div className="space-y-3 mb-4">
                          <h2 className="text-lg font-semibold text-white">Showcase Style</h2>
                          <div className="grid grid-cols-2 gap-3">
                            {/* Cinematic Orbit Style */}
                            <div
                              onClick={() => updateForm("largeProductStyle", "cinematic")}
                              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                form.largeProductStyle === "cinematic"
                                  ? "bg-emerald-500/10 border-emerald-500 shadow-lg shadow-emerald-500/20"
                                  : "bg-white/5 border-white/10 hover:border-white/30"
                              }`}
                              data-testid="large-product-style-cinematic"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <Video className={`w-5 h-5 ${form.largeProductStyle === "cinematic" ? "text-emerald-400" : "text-white/60"}`} />
                                <span className={`font-semibold text-sm ${form.largeProductStyle === "cinematic" ? "text-emerald-400" : "text-white"}`}>
                                  Cinematic Orbit
                                </span>
                              </div>
                              <p className="text-white/50 text-xs">
                                Camera smoothly orbits around the product with dramatic angles
                              </p>
                            </div>

                            {/* Talking Presenter Style */}
                            <div
                              onClick={() => updateForm("largeProductStyle", "presenter")}
                              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                form.largeProductStyle === "presenter"
                                  ? "bg-cyan-500/10 border-cyan-500 shadow-lg shadow-cyan-500/20"
                                  : "bg-white/5 border-white/10 hover:border-white/30"
                              }`}
                              data-testid="large-product-style-presenter"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <User className={`w-5 h-5 ${form.largeProductStyle === "presenter" ? "text-cyan-400" : "text-white/60"}`} />
                                <span className={`font-semibold text-sm ${form.largeProductStyle === "presenter" ? "text-cyan-400" : "text-white"}`}>
                                  Talking Presenter
                                </span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 font-medium">NEW</span>
                              </div>
                              <p className="text-white/50 text-xs">
                                Presenter walks around & interacts with the product while speaking
                              </p>
                            </div>
                          </div>

                          {/* Presenter Options when presenter style selected */}
                          {form.largeProductStyle === "presenter" && (
                            <div className="space-y-4 pt-4 border-t border-white/10">
                              <h3 className="text-md font-semibold text-white flex items-center gap-2">
                                <User className="w-4 h-4 text-cyan-400" />
                                Presenter Settings
                              </h3>
                              
                              {/* Presenter Image Upload */}
                              <div>
                                <Label className="text-white/70 text-xs mb-2 block">Presenter Image (Optional)</Label>
                                <label
                                  className={`flex items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer transition-all ${
                                    form.presenterImagePreview
                                      ? "border-cyan-500/50 bg-cyan-500/10"
                                      : "border-white/20 hover:border-white/40 bg-white/5"
                                  }`}
                                >
                                  {form.presenterImagePreview ? (
                                    <div className="relative w-full">
                                      <img
                                        src={form.presenterImagePreview}
                                        alt="Presenter"
                                        className="w-full max-h-48 rounded-lg object-contain"
                                      />
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          updateForm("presenterImage", null);
                                          updateForm("presenterImagePreview", null);
                                        }}
                                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 rounded-full p-1.5 text-white shadow-lg"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="text-center py-2">
                                      <Upload className="w-6 h-6 mx-auto text-white/40 mb-1" />
                                      <p className="text-white/60 text-sm">Upload presenter image</p>
                                      <p className="text-white/40 text-xs mt-1">AI will generate if not provided</p>
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePresenterImageUpload}
                                    className="hidden"
                                    data-testid="large-product-presenter-upload"
                                  />
                                </label>
                              </div>

                              {/* Info Box */}
                              <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                                <p className="text-white/80 text-xs">
                                  <strong className="text-cyan-400">Tip:</strong> The presenter will actively interact with your product - opening doors, pointing at features, and speaking directly to the camera with lip-sync.
                                </p>
                              </div>
                              
                              {/* Product Category Selector */}
                              <div className="pt-4 border-t border-white/10">
                                <Label className="text-white/70 text-xs mb-2 block">Product Category (Interaction Style)</Label>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    { id: "car", label: "Car/Vehicle", icon: "🚗", desc: "Open doors, sit inside, touch features" },
                                    { id: "furniture", label: "Furniture", icon: "🛋️", desc: "Sit, touch fabric, demonstrate comfort" },
                                    { id: "bed", label: "Bed/Mattress", icon: "🛏️", desc: "Lie down, touch, show comfort" },
                                    { id: "property", label: "Property", icon: "🏠", desc: "Walk through, open doors, point" },
                                    { id: "appliance", label: "Appliance", icon: "🔌", desc: "Touch controls, open, demonstrate" },
                                    { id: "gym", label: "Gym Equipment", icon: "🏋️", desc: "Touch, demonstrate, adjust" },
                                    { id: "travel", label: "Travel/Luggage", icon: "🧳", desc: "Wheel, open, show compartments" },
                                    { id: "outdoor_gear", label: "Outdoor Gear", icon: "🏕️", desc: "Set up, demonstrate, pack" },
                                  ].map((cat) => (
                                    <button
                                      key={cat.id}
                                      type="button"
                                      onClick={() => updateForm("largeProductCategory", cat.id)}
                                      className={`p-3 rounded-lg border text-left transition-all ${
                                        form.largeProductCategory === cat.id
                                          ? "border-cyan-500 bg-cyan-500/20"
                                          : "border-white/10 bg-white/5 hover:border-white/30"
                                      }`}
                                      data-testid={`product-category-${cat.id}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{cat.icon}</span>
                                        <span className={`text-sm font-medium ${form.largeProductCategory === cat.id ? "text-cyan-400" : "text-white"}`}>
                                          {cat.label}
                                        </span>
                                      </div>
                                      <p className="text-white/50 text-xs mt-1">{cat.desc}</p>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Cinematic Mode Info */}
                          {form.largeProductStyle === "cinematic" && (
                            <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                              <div className="flex items-start gap-3">
                                <Car className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-white text-sm font-medium">Cinematic Product Showcase</p>
                                  <p className="text-white/60 text-xs mt-1">
                                    Perfect for cars, furniture, property & large items. Upload images for cinematic motion sequences with smooth camera movements and professional voiceover.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Voiceover Settings - for cinematic large_product or product_only */}
                      {(form.videoMode === "product_only" || (form.videoMode === "large_product" && form.largeProductStyle === "cinematic")) && (
                        <>
                          <h2 className="text-lg font-semibold text-white pt-2">Voiceover Settings</h2>
                          <p className="text-white/50 text-sm -mt-2">
                            {form.videoMode === "large_product" 
                              ? "Select the voice persona for your product showcase narration"
                              : "Select the voice persona for your product narration"
                            }
                          </p>
                          
                          {/* Persona/Accent for Voice */}
                          <div>
                            <Label className="text-white/70 text-xs mb-2 block">Voice Accent</Label>
                            <div className="flex flex-wrap gap-2">
                              {PERSONAS.map((p) => (
                                <SelectBtn
                                  key={p.id}
                                  selected={form.persona === p.id}
                                  onClick={() => updateForm("persona", p.id)}
                                >
                                  {p.flag} {p.label}
                                </SelectBtn>
                              ))}
                            </div>
                          </div>
                          
                          {/* Gender for Voice */}
                          <div>
                            <Label className="text-white/70 text-xs mb-2 block">Voice Gender</Label>
                            <div className="flex gap-2">
                              {GENDERS.filter(g => g.id !== "neutral").map((g) => (
                                <SelectBtn
                                  key={g.id}
                                  selected={form.gender === g.id}
                                  onClick={() => updateForm("gender", g.id)}
                                >
                                  {g.label}
                                </SelectBtn>
                              ))}
                            </div>
                          </div>

                          {/* Use My Cloned Voice - Available for Product-Only mode */}
                          {clonedVoices.length > 0 && (
                            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg p-4 border border-purple-500/30">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <Label className="text-white flex items-center gap-2">
                                    <Mic className="w-4 h-4 text-purple-400" />
                                    Use My Cloned Voice
                                  </Label>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Use your cloned voice for the product narration
                                  </p>
                                </div>
                                <Switch
                                  checked={useClonedVoice}
                                  onCheckedChange={(checked) => {
                                    setUseClonedVoice(checked);
                                    if (checked) setUseAfricanLanguage(false); // Can't use both
                                  }}
                                  data-testid="use-cloned-voice-toggle-product"
                                />
                              </div>
                              
                              {useClonedVoice && (
                                <div className="space-y-2">
                                  <Label className="text-white/70 text-xs">Select Voice</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {clonedVoices.map((voice) => (
                                      <SelectBtn
                                        key={voice.voice_id}
                                        selected={selectedClonedVoice === voice.voice_id}
                                        onClick={() => setSelectedClonedVoice(voice.voice_id)}
                                      >
                                        🎙️ {voice.name}
                                      </SelectBtn>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* African Languages - Khaya AI - For Product-Only and Cinematic Large Product */}
                          {africanLanguages.length > 0 && (
                            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg p-4 border border-emerald-500/30">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <Label className="text-white flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-emerald-400" />
                                    Use African Language
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">NEW</span>
                                  </Label>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Voiceover in Twi, Yoruba, Swahili & more African languages
                                  </p>
                                </div>
                                <Switch
                                  checked={useAfricanLanguage}
                                  onCheckedChange={(checked) => {
                                    setUseAfricanLanguage(checked);
                                    if (checked) setUseClonedVoice(false); // Can't use both
                                  }}
                                  data-testid="use-african-language-toggle-product"
                                />
                              </div>
                              
                              {useAfricanLanguage && (
                                <div className="space-y-3">
                                  {/* Favorites section header */}
                                  {favoriteLanguages.length > 0 && (
                                    <div className="flex items-center gap-2 text-xs text-amber-400/80">
                                      <Star className="w-3 h-3 fill-amber-400" />
                                      <span>Favorites shown first</span>
                                    </div>
                                  )}
                                  
                                  <Label className="text-white/70 text-xs">Select Language (click ⭐ to pin, 🔊 to preview)</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {sortedAfricanLanguages.map((lang) => {
                                      const isFav = favoriteLanguages.includes(lang.id);
                                      const isPlaying = playingVoicePreview === lang.id;
                                      const isLoading = loadingVoicePreview === lang.id;
                                      
                                      return (
                                        <div 
                                          key={lang.id} 
                                          className={`relative group flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer ${
                                            selectedAfricanLanguage === lang.id
                                              ? "bg-cyan-500/30 text-cyan-300 border border-cyan-400/50"
                                              : "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 hover:border-white/20"
                                          } ${isFav ? "ring-1 ring-amber-400/30" : ""}`}
                                          onClick={() => setSelectedAfricanLanguage(lang.id)}
                                          data-testid={`african-lang-product-${lang.id}`}
                                        >
                                          {/* Star (Favorite) button */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleFavoriteLanguage(lang.id);
                                            }}
                                            className={`p-0.5 rounded transition-colors ${
                                              isFav 
                                                ? "text-amber-400 hover:text-amber-300" 
                                                : "text-white/30 hover:text-amber-400/70"
                                            }`}
                                            title={isFav ? "Remove from favorites" : "Add to favorites"}
                                          >
                                            <Star className={`w-3 h-3 ${isFav ? "fill-amber-400" : ""}`} />
                                          </button>
                                          
                                          {/* Language name */}
                                          <span className="mx-1">{lang.name}</span>
                                          
                                          {/* Play preview button */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              playVoicePreview(lang.id);
                                            }}
                                            className={`p-0.5 rounded transition-colors ${
                                              isPlaying 
                                                ? "text-emerald-400" 
                                                : "text-white/30 hover:text-emerald-400/70"
                                            }`}
                                            title="Preview voice"
                                            disabled={isLoading}
                                          >
                                            {isLoading ? (
                                              <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : isPlaying ? (
                                              <VolumeX className="w-3 h-3" />
                                            ) : (
                                              <Volume2 className="w-3 h-3" />
                                            )}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Presenter Mode - Accent and Age/Gender selection */}
                      {form.videoMode === "large_product" && form.largeProductStyle === "presenter" && (
                        <>
                          <h2 className="text-lg font-semibold text-white pt-2">Presenter Voice & Appearance</h2>
                          
                          {/* Persona/Accent for Voice */}
                          <div>
                            <Label className="text-white/70 text-xs mb-2 block">Voice Accent</Label>
                            <div className="flex flex-wrap gap-2">
                              {PERSONAS.map((p) => (
                                <SelectBtn
                                  key={p.id}
                                  selected={form.persona === p.id}
                                  onClick={() => updateForm("persona", p.id)}
                                >
                                  {p.flag} {p.label}
                                </SelectBtn>
                              ))}
                            </div>
                          </div>

                          {/* Age Group */}
                          <div>
                            <Label className="text-white/70 text-xs mb-2 block">Presenter Age</Label>
                            <div className="flex gap-2">
                              {AGE_GROUPS.map((a) => (
                                <SelectBtn
                                  key={a.id}
                                  selected={form.ageGroup === a.id}
                                  onClick={() => updateForm("ageGroup", a.id)}
                                >
                                  {a.label}
                                </SelectBtn>
                              ))}
                            </div>
                          </div>

                          {/* Gender */}
                          <div>
                            <Label className="text-white/70 text-xs mb-2 block">Presenter Gender</Label>
                            <div className="flex gap-2">
                              {GENDERS.filter(g => g.id !== "neutral").map((g) => (
                                <SelectBtn
                                  key={g.id}
                                  selected={form.gender === g.id}
                                  onClick={() => updateForm("gender", g.id)}
                                >
                                  {g.label}
                                </SelectBtn>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Product-Only Mode Info */}
                      {form.videoMode === "product_only" && (
                        <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
                          <div className="flex items-start gap-3">
                            <Package className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white text-sm font-medium">Product Showcase Mode</p>
                              <p className="text-white/60 text-xs mt-1">
                                Your video will feature cinematic product shots with smooth camera movements and professional ElevenLabs voiceover narration.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </motion.div>
              )}
              
              {/* Step 2: Scene */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <h2 className="text-lg font-semibold text-white">Scene & Location</h2>
                  
                  {/* Grouped scene selector with categories */}
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {["Home", "Business", "Retail", "Travel", "Media", "Hospitality", "Specialty"].map((category) => {
                      const categoryScenes = SCENES.filter(s => s.category === category);
                      if (categoryScenes.length === 0) return null;
                      return (
                        <div key={category}>
                          <p className="text-xs text-white/40 mb-2 font-medium uppercase tracking-wide">{category}</p>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {categoryScenes.map((s) => {
                              const Icon = s.icon;
                              return (
                                <button
                                  key={s.id}
                                  onClick={() => {
                                    updateForm("scene", s.id);
                                    // Auto-select "Replace Background" when user chooses a scene
                                    updateForm("backgroundMode", "scene");
                                  }}
                                  className={`flex flex-col items-center p-2 rounded-lg border transition-all ${
                                    form.scene === s.id
                                      ? "bg-cyan-500/20 border-cyan-500 text-cyan-400"
                                      : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
                                  }`}
                                >
                                  <Icon className="w-4 h-4 mb-1" />
                                  <span className="text-[10px] leading-tight text-center">{s.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Background Mode Toggle - Only show when presenter image is uploaded */}
                  {form.presenterImage && (
                    <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-white/70 text-sm">Background Mode</Label>
                      </div>
                      <p className="text-white/40 text-xs mb-3">Choose how to handle your presenter's background</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => updateForm("backgroundMode", "original")}
                          className={`flex flex-col items-center p-3 rounded-lg border transition-all ${
                            form.backgroundMode === "original"
                              ? "bg-cyan-500/20 border-cyan-500 text-cyan-400"
                              : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
                          }`}
                          data-testid="bg-mode-original"
                        >
                          <Image className="w-5 h-5 mb-1" />
                          <span className="text-xs font-medium">Keep Original</span>
                          <span className="text-[10px] text-white/40 mt-1">Use presenter's background</span>
                        </button>
                        <button
                          onClick={() => updateForm("backgroundMode", "scene")}
                          className={`flex flex-col items-center p-3 rounded-lg border transition-all ${
                            form.backgroundMode === "scene"
                              ? "bg-cyan-500/20 border-cyan-500 text-cyan-400"
                              : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
                          }`}
                          data-testid="bg-mode-scene"
                        >
                          <Wand2 className="w-5 h-5 mb-1" />
                          <span className="text-xs font-medium">Use Selected Scene</span>
                          <span className="text-[10px] text-white/40 mt-1">AI removes & replaces BG</span>
                        </button>
                      </div>
                      {form.backgroundMode === "scene" && (
                        <p className="text-amber-400/80 text-xs mt-2 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Background will be replaced with {SCENES.find(s => s.id === form.scene)?.label || "selected scene"}
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Step 3: Product */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <h2 className="text-lg font-semibold text-white">Product Details</h2>
                  
                  {/* Product Image Upload - Multiple Images */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-white/70 text-xs">Product Images (up to 5; different colours? tap "Show this one")</Label>
                      <span className="text-white/40 text-xs">{form.productImages.length}/5</span>
                    </div>
                    
                    {/* Image Grid */}
                    {form.productImagePreviews.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        {form.productImagePreviews.map((preview, index) => (
                          <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-cyan-500/30">
                            <img
                              src={preview}
                              alt={`Product ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeProductImage(index);
                              }}
                              className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 rounded-full p-1 text-white shadow-lg"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            {form.productImagePreviews.length > 1 && (index === 0 && form.showFirstImage ? (
                              <span data-testid={`product-image-shown-${index}`} className="absolute bottom-1 left-1 bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                                <Check className="w-3 h-3" /> Showing
                              </span>
                            ) : (
                              <button type="button" data-testid={`product-image-show-${index}`}
                                onClick={(e) => { e.stopPropagation(); showProductImage(index); }}
                                className="absolute bottom-1 left-1 bg-black/70 hover:bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded transition-colors">
                                Show this one
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    {form.productImages.length < 5 && (
                      <div className="space-y-2">
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all border-white/20 hover:border-white/40 hover:bg-white/5"
                        >
                          <div className="py-2">
                            <Upload className="w-6 h-6 mx-auto text-white/40 mb-1" />
                            <p className="text-white/60 text-sm">
                              {form.productImages.length === 0 
                                ? "Upload product images" 
                                : "Add more angles"}
                            </p>
                            <p className="text-white/30 text-xs">PNG, JPG up to 10MB each</p>
                          </div>
                        </div>
                        <PickFromGalleryButton 
                          onSelect={handleGalleryImageSelect}
                          className="w-full"
                          size="sm"
                        />
                        <MediaPickerButton
                          onSelect={handleGalleryImageSelect}
                          mediaType="image"
                          multiple={false}
                          allowUpload={true}
                          buttonText="Media Library"
                          buttonClassName="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-sm text-purple-300 transition-colors mt-2"
                        />
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleProductImageUpload}
                    className="hidden"
                  />
                  
                  {/* Product URL */}
                  <div>
                    <Label className="text-white/70 text-xs mb-1 block">Product URL (optional)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={form.productUrl}
                        onChange={(e) => updateForm("productUrl", e.target.value)}
                        placeholder="https://..."
                        className="bg-white/5 border-white/10 text-white h-9"
                      />
                      <Button
                        onClick={fetchProductInfo}
                        variant="outline"
                        size="sm"
                        className="border-white/20 text-white/70 hover:bg-white/10"
                        data-testid="fetch-product-btn"
                      >
                        Fetch
                      </Button>
                    </div>
                    
                    {/* Screenshot Upload Option */}
                    <div className="mt-2 p-3 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg border border-purple-500/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white/80 text-xs font-medium">Can't scrape URL?</p>
                          <p className="text-white/50 text-[10px]">Upload a product screenshot instead</p>
                        </div>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) analyzeProductScreenshot(file);
                              e.target.value = '';
                            }}
                            data-testid="screenshot-upload-input"
                          />
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-xs rounded-lg transition-colors">
                            <Camera className="w-3.5 h-3.5" />
                            Upload Screenshot
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Product Name */}
                  <div>
                    <Label className="text-white/70 text-xs mb-1 block">Product Name *</Label>
                    <Input
                      value={form.productName}
                      onChange={(e) => updateForm("productName", e.target.value)}
                      placeholder="e.g., GlowSkin Vitamin C Serum"
                      className="bg-white/5 border-white/10 text-white h-9"
                    />
                  </div>
                  
                  {/* Product Description */}
                  <div>
                    <Label className="text-white/70 text-xs mb-1 block">Description</Label>
                    <Textarea
                      value={form.productDescription}
                      onChange={(e) => {
                        updateForm("productDescription", e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      placeholder="Key benefits and features..."
                      className="bg-white/5 border-white/10 text-white min-h-[60px] resize-none overflow-hidden"
                      style={{ height: 'auto' }}
                    />
                  </div>

                  <MustShowField value={form.mustShow} onChange={(v) => updateForm("mustShow", v)}
                    productName={form.productName} productDescription={form.productDescription}
                    productImage={form.productImages[0]} />

                  <LogoUpload file={form.logoImage} onChange={(f) => updateForm("logoImage", f)} />
                  
                  {/* Virtual Try-On - For Fashion/Clothing Products */}
                  {/* Works with or without presenter image - AI will generate presenter if needed */}
                  {form.productImages.length > 0 && (form.videoMode === "talking_head" || form.videoMode === "presenter_voiceover") && (
                    <div className="p-3 bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-lg border border-pink-500/20">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Shirt className="w-4 h-4 text-pink-400" />
                            <span className="text-white/90 text-sm font-medium">Virtual Try-On</span>
                            <span className="text-[10px] text-pink-300 bg-pink-500/20 px-1.5 py-0.5 rounded">FASHION</span>
                          </div>
                          <p className="text-white/50 text-xs mt-1">
                            {form.presenterImage 
                              ? "Make presenter wear the product instead of holding it"
                              : "AI will create a model wearing your product"}
                          </p>
                        </div>
                        <Switch
                          checked={form.enableVirtualTryon}
                          onCheckedChange={(v) => updateForm("enableVirtualTryon", v)}
                          data-testid="virtual-tryon-toggle"
                        />
                      </div>
                      {form.enableVirtualTryon && (
                        <div className="mt-3 space-y-3">
                          {/* Product Type Selector */}
                          <div>
                            <Label className="text-white/70 text-xs mb-2 block">What type of product?</Label>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { id: "clothing", label: "Clothing", icon: "👕", desc: "Shirts, dresses, tops" },
                                { id: "shoes", label: "Shoes", icon: "👟", desc: "Sneakers, heels, boots" },
                                { id: "jewelry", label: "Jewelry", icon: "💎", desc: "Necklaces, rings, earrings" },
                                { id: "wig", label: "Wigs/Hair", icon: "💇", desc: "Wigs, extensions" },
                                { id: "accessories", label: "Accessories", icon: "👜", desc: "Bags, hats, glasses" },
                                { id: "watch", label: "Watches", icon: "⌚", desc: "Wrist watches" },
                              ].map((type) => (
                                <div
                                  key={type.id}
                                  onClick={() => updateForm("tryonProductType", type.id)}
                                  className={`p-2 rounded-lg border cursor-pointer transition-all text-center ${
                                    form.tryonProductType === type.id
                                      ? "bg-pink-500/20 border-pink-500 shadow-lg shadow-pink-500/10"
                                      : "bg-white/5 border-white/10 hover:border-pink-500/50"
                                  }`}
                                >
                                  <span className="text-lg">{type.icon}</span>
                                  <p className={`text-xs font-medium ${form.tryonProductType === type.id ? "text-pink-300" : "text-white/80"}`}>
                                    {type.label}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                          <p className="text-pink-300/70 text-[10px]">
                            {form.tryonProductType === "shoes" && "Camera will focus on feet and lower body to showcase the shoes"}
                            {form.tryonProductType === "jewelry" && "Camera will focus on the jewelry area (neck, ears, hands) for close-up shots"}
                            {form.tryonProductType === "wig" && "Camera will focus on head and hair for the best wig showcase"}
                            {form.tryonProductType === "watch" && "Camera will focus on wrist and arm movements to showcase the watch"}
                            {form.tryonProductType === "accessories" && "Camera will highlight the accessory being worn"}
                            {form.tryonProductType === "clothing" && "Full body showcase with focus on the clothing item"}
                          </p>
                          <div className="mt-3">
                            <SecondItemPicker value={form.secondItem} onChange={(v) => updateForm("secondItem", v)} />
                          </div>
                          {OUTFIT_PREVIEW_TYPES.includes(form.tryonProductType) && (
                            <label className="flex items-center gap-2 mt-3 text-xs text-white/70 cursor-pointer">
                              <Switch data-testid="skip-outfit-preview-toggle" checked={form.skipOutfitPreview}
                                onCheckedChange={(v) => updateForm("skipOutfitPreview", v)} />
                              Skip the try-on photo check (start the video straight away)
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Virtual Try-On Hint - Show when mode is compatible but missing images */}
                  {(form.videoMode === "talking_head" || form.videoMode === "presenter_voiceover") && 
                   (form.productImages.length === 0 || !form.presenterImage) && (
                    <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                      <div className="flex items-center gap-2 text-white/50">
                        <Shirt className="w-4 h-4" />
                        <span className="text-xs">
                          <strong className="text-pink-400">Virtual Try-On</strong> available for fashion products! 
                          {!form.presenterImage && " Upload a presenter image"}
                          {!form.presenterImage && form.productImages.length === 0 && " and"}
                          {form.productImages.length === 0 && " add product images"} to enable.
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Video Duration */}
                  <div>
                    <Label className="text-white/70 text-xs mb-2 block">Video Duration</Label>
                    <div className="flex gap-2">
                      {VIDEO_DURATIONS.filter((d) => !isStandard || d === 15 || d === 30).map((d) => (
                        <SelectBtn
                          key={d}
                          selected={form.videoDuration === d}
                          onClick={() => updateForm("videoDuration", d)}
                        >
                          {d}s
                        </SelectBtn>
                      ))}
                    </div>
                    {isStandard && (
                      <p data-testid="ugc-standard-length-note" className="text-xs text-emerald-400/80 mt-2">
                        {form.videoDuration === 30
                          ? "Standard 30s: two 15s shots joined into one ad, about 80 spoken words"
                          : "Standard quality: 15s and about 40 spoken words"}
                      </p>
                    )}
                    {!isStandard && form.videoDuration === 15 && form.videoMode === 'talking_head' && (
                      <p className="text-xs text-cyan-400/80 mt-2 flex items-center gap-1">
                        <span className="text-cyan-400">ℹ</span>
                        15s talking head ≈ 13s of speech; gallery images are added after the speech
                      </p>
                    )}
                    {form.videoDuration === 30 && !isStandard && (
                      <p className="text-xs text-amber-400/80 mt-2 flex items-center gap-1">
                        <span className="text-amber-400">ℹ️</span>
                        {form.videoMode === 'talking_head' 
                          ? '30s Hybrid Mode: 15s talking head (lip-sync) + 15s cinematic product B-roll with voiceover'
                          : '30s videos are created by stitching two 15s clips. Script will be split accordingly.'}
                      </p>
                    )}
                  </div>
                  
                  {/* AI Script */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-white/70 text-xs">AI Script</Label>
                      <Button
                        onClick={generateScript}
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-cyan-400 hover:text-cyan-300"
                      >
                        <Wand2 className="w-3 h-3 mr-1" />
                        Generate
                      </Button>
                    </div>
                    <Textarea
                      ref={attachScriptInput}
                      data-testid="ugc-script-input"
                      value={form.script}
                      onChange={(e) => {
                        updateForm("script", e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      placeholder="AI will generate a script based on your product..."
                      className="bg-white/5 border-white/10 text-white min-h-[180px] resize-vertical text-sm"
                    />
                    <div className="mt-1 flex justify-end">
                      <ScriptFitIndicator fit={scriptFit} checking={checkingFit} />
                    </div>
                  </div>
                  
                  {/* Hooks */}
                  {form.hooks.length > 0 && (
                    <div>
                      <Label className="text-white/70 text-xs mb-2 block">Select Hook</Label>
                      <div className="space-y-2">
                        {form.hooks.map((hook, i) => (
                          <button
                            key={i}
                            onClick={() => updateForm("selectedHook", i)}
                            className={`w-full text-left p-2 rounded-lg text-sm transition-all ${
                              form.selectedHook === i
                                ? "bg-cyan-500/20 border-cyan-500 border text-white"
                                : "bg-white/5 border-white/10 border text-white/70 hover:bg-white/10"
                            }`}
                          >
                            {hook}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* Step 4: Motion & Generate */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <h2 className="text-lg font-semibold text-white">Motion & Style</h2>
                  
                  {/* Motion Style */}
                  <div>
                    <Label className="text-white/70 text-xs mb-2 block">Camera Style</Label>
                    <div className="flex gap-2">
                      {MOTION_STYLES.map((m) => {
                        const Icon = m.icon;
                        return (
                          <button
                            key={m.id}
                            onClick={() => updateForm("motionStyle", m.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                              form.motionStyle === m.id
                                ? "bg-cyan-500/20 border-cyan-500 text-cyan-400"
                                : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="text-sm">{m.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Product Gallery Outro */}
                  <div>
                    <Label className="text-white/70 text-xs mb-2 block">
                      Product Gallery Outro
                      <span className="text-white/40 ml-2">(AI-generated product shots at end)</span>
                    </Label>
                    <div className="flex gap-2">
                      {GALLERY_OPTIONS.map((opt) => (
                        <SelectBtn
                          key={opt.count}
                          selected={form.galleryOutro === opt.count}
                          onClick={() => updateForm("galleryOutro", opt.count)}
                        >
                          {opt.label}
                        </SelectBtn>
                      ))}
                    </div>
                    {form.galleryOutro > 0 && (
                      <p className="text-white/40 text-xs mt-2">
                        +{form.galleryOutro * 3}s added • Product in different scenes & angles
                      </p>
                    )}
                  </div>
                  
                  {/* Options */}
                  <div className="space-y-3 py-3 border-t border-white/10">
                    <p className="text-white/50 text-xs uppercase tracking-wider">Options</p>
                    
                    {/* Auto Captions Toggle */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Type className="w-5 h-5 text-purple-400" />
                          <div>
                            <Label className="text-white text-sm font-medium">Auto Captions</Label>
                            <p className="text-white/50 text-xs">Add captions to your video</p>
                          </div>
                        </div>
                        <Switch
                          checked={form.enableCaptions}
                          onCheckedChange={(v) => updateForm("enableCaptions", v)}
                          data-testid="auto-captions-toggle"
                        />
                      </div>
                      
                      {/* Caption Style Selection - shown when captions enabled */}
                      {form.enableCaptions && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pl-3"
                        >
                          <Label className="text-white/70 text-xs mb-2 block">Caption Style</Label>
                          <CaptionStyleSelector
                            value={form.captionStyle}
                            onChange={(style) => updateForm("captionStyle", style)}
                            compact={true}
                          />
                        </motion.div>
                      )}
                    </div>
                  </div>
                  
                  {/* Summary */}
                  <div className="bg-white/5 rounded-lg p-3 text-xs text-white/60">
                    <p><strong className="text-white">Mode:</strong> {
                      form.videoMode === "talking_head" ? "Talking Head Ad" :
                      form.videoMode === "presenter_voiceover" ? "Presenter with Voiceover" :
                      form.videoMode === "large_product" ? "Large Product Showcase" :
                      "Product-Only Ad"
                    }</p>
                    {(form.videoMode === "talking_head" || form.videoMode === "presenter_voiceover") && (
                      <p><strong className="text-white">Persona:</strong> {PERSONAS.find(p => p.id === form.persona)?.label} {form.gender}, {AGE_GROUPS.find(a => a.id === form.ageGroup)?.label}</p>
                    )}
                    {(form.videoMode === "product_only" || form.videoMode === "large_product") && (
                      <p><strong className="text-white">Voiceover:</strong> {PERSONAS.find(p => p.id === form.persona)?.label} {form.gender}</p>
                    )}
                    <p><strong className="text-white">Scene:</strong> {SCENES.find(s => s.id === form.scene)?.label} | <strong className="text-white">Quality:</strong> {isStandard ? "Standard" : "Premium"} | <strong className="text-white">Duration:</strong> {form.videoDuration}s{form.galleryOutro > 0 ? ` + ${form.galleryOutro * 3}s gallery` : ""}</p>
                    <p><strong className="text-white">Product:</strong> {form.productName || "Not set"}</p>
                    <p><strong className="text-white">Reference Images:</strong> {form.productImages.length} image(s){form.videoMode === "large_product" ? " for cinematic motion" : " for element binding"}</p>
                    {form.galleryOutro > 0 && (
                      <p><strong className="text-white">Gallery:</strong> {form.galleryOutro} product images at end</p>
                    )}
                  </div>
                  
                  {/* Generate Button */}
                  <div className="flex gap-3">
                    <Button
                      data-testid="generate-ugc-ad-btn"
                      onClick={generateVideo}
                      disabled={isGenerating || !form.productName}
                      className="flex-1 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black font-bold rounded-full shadow-lg shadow-cyan-500/25"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 mr-2" />
                          Generate UGC Ad · {ugcAdCost(form.ugcQuality, form.videoDuration)} credits
                          {qualityOptions?.credits_left != null && (
                            <span data-testid="ugc-credits-left" className="ml-1 font-normal opacity-70">({qualityOptions.credits_left} left)</span>
                          )}
                        </>
                      )}
                    </Button>
                    
                    {/* Create New Video Button */}
                    <Button
                      onClick={resetForm}
                      disabled={isGenerating}
                      variant="outline"
                      className="h-12 px-4 border-white/20 hover:bg-white/10 text-white rounded-full"
                      title="Start fresh with a new video"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      New Video
                    </Button>
                  </div>
                  <LowCreditNudge
                    cost={ugcAdCost(form.ugcQuality, form.videoDuration)}
                    left={qualityOptions?.credits_left}
                    onBuy={async () => {
                      try { await saveUgcDraft(form, step); } catch { toast.error("Couldn't save your ad settings."); }
                      navigate("/pricing#credits");
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6 pt-4 border-t border-white/10">
              <Button
                onClick={prevStep}
                disabled={step === 1}
                variant="ghost"
                className="text-white/60 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              {step < 4 && (
                <Button
                  onClick={nextStep}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Generation Progress - Compact Bar */}
        {isGenerating && (
          <div className="mt-6 bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-4">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white font-medium">{status}</span>
                  <span className="text-cyan-400 font-medium">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </div>
          </div>
        )}
        
        {/* Video Ready - Download/Share */}
        {videoUrl && !isGenerating && (
          <div className="mt-6 space-y-4">
            {/* Success card */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl p-4 border border-cyan-500/20">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Video Ready!</p>
                    <p className="text-white/60 text-sm">Your UGC ad has been generated</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={downloadVideo}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    data-testid="download-video-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(videoUrl);
                      toast.success("Video URL copied!");
                    }}
                    variant="outline"
                    className="border-white/20 text-white/70 hover:bg-white/10"
                    data-testid="share-video-btn"
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Navigation Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
              <Button
                onClick={() => {
                  setVideoUrl(null);
                  setVideoId(null);
                  setProgress(0);
                  setStatus("");
                  setStep(1);
                  setForm({
                    productName: "",
                    productDescription: "",
                    targetAudience: "",
                    callToAction: "",
                    scriptText: "",
                    adStyle: "testimonial",
                    hookStyle: "question",
                    generatedHooks: [],
                    selectedHook: "",
                    presenterImageUrl: null,
                    backgroundType: "scene",
                    selectedScene: null,
                    selectedScenePrompt: "",
                    voiceTier: "free",
                    selectedVoice: null,
                    talkingHeadEnabled: false,
                    musicId: null,
                  });
                }}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white font-medium"
                data-testid="create-new-video-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Video
              </Button>
              <Button
                onClick={() => navigate('/videos')}
                variant="outline"
                className="flex-1 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                data-testid="goto-library-btn"
              >
                <Film className="w-4 h-4 mr-2" />
                Go to Video Library
              </Button>
            </div>
            
            {/* A/B Testing CTA */}
            <div 
              onClick={() => setAbTestModalOpen(true)}
              className="p-4 rounded-xl border-2 border-dashed border-purple-500/30 hover:border-purple-500/60 cursor-pointer transition-all bg-purple-500/5 hover:bg-purple-500/10"
              data-testid="ab-testing-cta"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center">
                  <FlaskConical className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium">Create A/B Test Variations</h3>
                  <p className="text-white/50 text-sm">Generate multiple versions to test different hooks, CTAs, and presenters</p>
                </div>
                <ChevronRight className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal for subscription quota errors */}
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        errorType={upgradeErrorType}
        errorData={upgradeErrorData}
        formData={form}
        returnPath="/ugc-studio"
      />
      
      {/* A/B Testing Modal */}
      <ABTestingModal
        open={abTestModalOpen}
        onClose={() => setAbTestModalOpen(false)}
        baseConfig={{
          persona: form.persona,
          ageGroup: form.ageGroup,
          gender: form.gender,
          emotion: form.emotion,
          scene: form.scene,
          motionStyle: form.motionStyle,
          videoDuration: form.videoDuration,
          videoStyle: form.videoStyle,
          galleryOutro: form.galleryOutro,
          productName: form.productName,
          productDescription: form.productDescription,
          script: form.script,
          hook: form.hooks[form.selectedHook] || "",
          enableCaptions: form.enableCaptions,
          enableLipSync: form.enableLipSync,
          videoMode: form.videoMode,
        }}
        onBatchCreated={(result) => {
          toast.success(`Created ${result.total_variations} variations!`);
        }}
        onGenerateVariation={(config) => ugcAPI.generateAd(buildAdPayload({
          persona: config.persona, ageGroup: config.ageGroup, gender: config.gender,
          emotion: config.emotion, scene: config.scene, script: config.script, hook: config.hook ?? (form.hooks[form.selectedHook] || ""),
        }))}
      />
      
      {/* Template Preview Gallery Modal */}
      <TemplatePreviewGallery
        isOpen={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        onSelectTemplate={handleSelectTemplate}
      />
      
      <OutfitPreviewDialog
        open={!!outfitPending}
        request={{ presenterImage: form.presenterImage, productImages: form.productImages, scene: form.scene,
          backgroundMode: form.backgroundMode, tryonProductType: form.tryonProductType,
          secondItem: form.secondItem?.images?.length ? form.secondItem : null }}
        onClose={() => setOutfitPending(null)}
        onApprove={(previewId) => { const o = outfitPending; setOutfitPending(null); runVideoGeneration({ ...o, outfitPreviewId: previewId }); }}
      />
      <ScriptFitPanel
        open={fitPanelOpen}
        fit={scriptFit}
        fitRequest={fitRequest}
        onClose={() => setFitPanelOpen(false)}
        onGenerate={handleFitPanelGenerate}
        onEdit={focusScriptInput}
      />
    </div>
  );
}
